/* eslint-disable max-len */
import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import Helmet from 'react-helmet';
import styled, {keyframes} from 'styled-components';

import {capitalize} from './../helpers';
import {
    colors,
    notWebMediaQuery,
    tabletMediaQuery,
    mobileMediaQuery,
    isMobile,
    breakpoints,
    PageBodyWrapper,
    useBreakpoint,
    notMobileMediaQuery,
    webMediaQuery,
} from './../styles';

import * as FavouriteActions from '../store/actions/favourite';
import * as ShowActions from '../store/actions/show';
import {showAccessed} from '../store/actions/recommendation';
import * as ToastActions from '../store/actions/toast';

import {contentUrlSolver} from './../helpers';

import Dropdown from './../components/Dropdown';
import Loader from './../components/Loader';

import HeartIcon from './../assets/favourites_icon.png';
import {ReactComponent as Checkmark} from '../assets/checkmark_icon.svg';

import Devices from './../assets/devices-asset.png';
import Apple from './../assets/apple.svg';
import GooglePlay from './../assets/google_play.svg';
import VideosGrid from '../components/VideosGrid';
import ContentRow from '../components/ContentRow';
import VectorLines from './../assets/show_lines.svg';
import LeftDecorator from './../assets/show_left_blob.png';
import RightDecorator from './../assets/show_right_blob.png';
import {getCurrentLang} from '../utils/i18n';

const CheckmarkIcon = styled(Checkmark)`
    margin-left: auto;
`;

const Container = styled(PageBodyWrapper)`
    margin: 0 auto 0 auto;
    padding: 0px;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: calc(100vh - 392px);
    width: 100%;
    box-sizing: border-box;

    & > #videos-grid {
        margin: 0;
        width: 100%;
    }

    ${notWebMediaQuery} {
        margin-top: 78px;
    }

    ${mobileMediaQuery} {
        max-width: 100vw;
        overflow-x: hidden;
        box-sizing: border-box;
    }
`;

const ShowBackground = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 480px;
    background-image: url(${props => props.url});
    background-color: #211921;
    background-size: 700px;
    background-position: 90% 0%;
    background-repeat: no-repeat;
    width: 100%;
    position: relative;
    margin: 0;
    

    ${tabletMediaQuery} {
        background-position: calc(90% + 100px) 0%;
        background-size: 600px;
        width: calc(100% + 80px);
        margin-left: -40px;
        margin-right: -40px;
    }

    ${mobileMediaQuery} {
        width: calc(100% + 50px);
        background-position: 50% 0%;
        background-size: 600px;
        margin-left: -25px;
        margin-right: -25px;

    `;

export const ShowLogo = styled.img`
    position: absolute;
    left: 200px;
    transform: translateY(calc(-50% - 45px));

    width: auto;
    height: 210px;

    ${tabletMediaQuery} {
        height: 198px;
        width: 198px;
        left: calc(50% - 245px);
    }

    ${mobileMediaQuery} {
        width: 126px;
        max-width: 47%;
        height: auto;
        position: initial;
        transform: initial;
        margin: auto auto 22px auto;
    }
`;

export const FavouriteIcon = styled.img`
    background-color: ${props => (props.active ? colors.pink : '#000')};
    width: 26px;
    height: 26px;
    border-radius: 100%;
    z-index: 3;
    padding: 0.6rem;
    margin-right: 2rem;
    object-fit: contain;
`;

const heartAnimation = keyframes`
    0% {
        transform: rotate(0deg);
    }

    50% {
        transform: rotate(40deg);
    }

    75% {
        transform:  rotate(-30deg);
    }



    100% {
        transform: rotate(0deg);
    }
`;

const AnimatedHeartIcon = styled.img`
    transform-origin: center bottom;
    &[data-animated='true'] {
        animation: ${heartAnimation} 0.5s ease;
    }
`;

export const FavouriteButton = styled.div`
    ${props => props.active && `background-color: ${colors.pink};`}
    ${props =>
        !props.active &&
        `background-color: ${colors.cartoonBlack};`}
    min-width: 24px;
    min-height: 24px;
    width: 24px;
    height: 24px;
    border-radius: 100%;
    position: absolute;
    bottom: 25px;
    right: 25px;
    z-index: 2;
    flex: 0;
    flex-basis: 24px;
    padding: 0.5rem;
    margin: 0 1rem 0 auto;
    justify-content: center;
    align-items: center;
    svg rect {
        fill: #fff;
    }
`;

const Title = styled.h1`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex: none;

    font-family: 'TTNormsProExtraBlack';
    font-size: 24px;
    font-weight: bold;
    line-height: 26px;
    color: #fff;
    text-transform: uppercase;
    padding: 0px;
    margin: 0px;
    flex: 1;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;

    @media screen and (min-width: ${breakpoints.mobile +
            1}px) and (max-width: ${breakpoints.sm}px) {
    }

    ${mobileMediaQuery} {
        font-size: 20px;

        word-break: break-word;
        white-space: pre-wrap;

        margin-bottom: 10px;
    }
`;

const Separator = styled.hr`
    height: 2px;
    width: 100%;
    margin-top: 0px;
    border: none;
    background: linear-gradient(
        90deg,
        #ffd93f 0%,
        #00aeef 20%,
        #ffd93f 40%,
        #ffd93f 40%,
        #ec008c 80%,
        #ffd93f 100%
    );

    ${tabletMediaQuery} {
        width: calc(100% + 80px);
        margin-left: -40px;
        margin-right: -40px;
    }

    ${mobileMediaQuery} {
        width: calc(100% + 50px);
        margin-left: -25px;
        margin-right: -25px;
    }
`;

const ButtonsRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    p {
        margin-left: 2rem;
        text-transform: uppercase;
        font-weight: bold;
    }
`;

const About = styled.div`
    text-align: center;
    margin: 0;
    color: #fff;
    padding: 0rem 6rem;
    ${props =>
        !props.logged &&
        `
        background-color: #fff;
        margin-top: 0;
        margin-bottom: -60px;
        p { color: ${colors.cartoonBlack}; }
    `}
    margin-top: 43px;
    margin-bottom: 60px;

    ${tabletMediaQuery} {
        margin-top: 23px;
        margin-bottom: 63px;
    }

    ${mobileMediaQuery} {
        padding: 0px 25px;
        margin: 57px 0 42px 0;
    }
`;

const AboutTitle = styled.h2`
    font-family: 'TTNormsProExtraBlack';
    font-size: 20px;
    line-height: 31px;
    text-align: center;
    margin: 0;

    ${mobileMediaQuery} {
        margin: 0px;
        font-size: 18px;
        text-align: left;
    }
`;

const Description = styled.p`
    font-size: 18px;
    line-height: 24px;
    margin-top: 22px;
    margin-bottom: 0;

    ${mobileMediaQuery} {
        margin-top: 5px;
        font-size: 16px;
        font-weight: normal;
        line-height: 22px;
        text-align: left;
    }
`;

const EndPoster = styled.img`
    margin: auto;
    margin-bottom: 56px;
    background-image: url(${LeftDecorator}), url(${RightDecorator});
    background-size: contain, contain;
    background-position: left, right;
    background-repeat: no-repeat;

    width: 100%;
    height: 440px;
    object-fit: contain;

    ${webMediaQuery} {
        background-image: url(${VectorLines}), url(${LeftDecorator}),
            url(${RightDecorator});
        background-size: contain, contain, contain;
        background-position: center, left, right top;
    }

    ${notWebMediaQuery} {
        max-width: 100%;
    }
`;

const Section = styled.div`
    color: ${colors.cartoonBlack};
    padding: 2rem 8rem;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-height: 360px;
    h1 {
        margin-top: 0;
        margin-bottom: 0;
        padding-bottom: 15px;
        font-size: 45px;
    }
    p {
        font-size: 18px;
        margin-top: 0;
        margin-bottom: 2rem;
    }
`;

const FavoriteShows = styled(Section)`
    background-color: #080008;
    color: #fff;
    justify-content: space-around;
    & > div:first-child p {
        text-align: center;
        margin-bottom: 10px;
    }
    & > div:first-child > div > img:first-child {
        margin-right: 19px;
    }
    & > div:nth-child(1) {
        flex: 0.5;
    }
    & > div:nth-child(2) {
        flex: 0.42;
    }
    padding: 74px 74px 47px 74px;
`;

const Col = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    justify-content: center;
    align-items: center;
`;

const Row = styled(Col)`
    flex-direction: row;
`;

const ShowBlob = styled.img`
position: absolute;
width: 100%;
left: 0px;
right: 0px;
bottom: 0px;
max-width: 1440px;
margin-left:auto;
margin-right:auto;
z-index: 1;
}`;

const TitleContainer = styled.div`
    flex-direction: row;
    display: flex;
    align-self: stretch;
    align-items: center;
    padding: 16px 25px;

    ${notMobileMediaQuery} {
        padding: 16px 40px;
    }
`;

const Padded = styled.div`
    padding: 0px 25px;
    width: 100%;
    ${notMobileMediaQuery} {
        padding: 0px 40px;
    }
`;

const ButtonsRowStyled = styled(ButtonsRow)`
    padding-bottom: 20px;
    align-items: flex-start;
    width: auto;
    background: linear-gradient(90deg, #f55a86 0%, #ec098c 100%);
    padding: 6px 10px;
    border-radius: 6px;
    margin-left: 12px;
`;

const ShowDetails = props => {
    props = {
        ...props,
        ...useSelector(state => state.episode),
    };

    const history = useHistory();
    const dispatch = useDispatch();
    const params = useParams();

    const shows = useSelector(state => state.show);
    const config = useSelector(state => state.config);
    const user = useSelector(state => state.user);
    const idp = user && user.idp;

    const {t} = useTranslation();

    const favourites = useSelector(state => state.favourite);
    const breakpoint = useBreakpoint();

    const [animateIcon, setAnimateIcon] = useState(false);
    const [favourite, setFavourite] = useState(false);
    const [pageState, setPageState] = useState('loading');
    const [currentShow, setCurrentShow] = useState(undefined);
    const [currentSeason, setCurrentSeason] = useState(undefined);
    const [episodes, setEpisodes] = useState(undefined);

    useEffect(() => {
        if (pageState === 'ready') {
            currentShow && dispatch(showAccessed(currentShow));

            window.turner_metadata.trackAction.push({
                type: 'pageview',
                data: {
                    pageName: `cnplus.com/shows/${params.show}`,
                    section: 'Show Detail',
                    subsection: 'nvs',
                    franchise: currentShow.title,
                    contenttype: 'Show Detail',
                    contenttitle: 'Show Detail',
                    presentationtemplate: 'desktop',
                    platform: 'web',
                    domain: 'cnplus.com',
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    brand: 'cartoon network',
                    pageviewevent: 1,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                },
            });
        }

        // eslint-disable-next-line
    }, [pageState]);

    useEffect(() => {
        if (shows.show ? !shows.show[params.show] : true) {
            loadShowDetails();
        }

        // eslint-disable-next-line
    }, [user.jwtToken]);

    const loadShowDetails = async () => {
        let response = await dispatch(ShowActions.loadShowDetails(params.show));

        if (response.data?.data?.show) {
            let showData = response.data.data.show;

            showData.metadata = {
                ...showData,
                contentId: showData.id,
                pictureUrl: showData.image,

                metadata: undefined,
                seasons: undefined,
            };

            showData.seasons.map(season => {
                season.contentId = season.id;
                season.metadata = {
                    ...season,
                    objectSubtype: season.type,
                    season: season.number,
                };

                return season;
            });

            setCurrentShow(showData);
        } else {
            history.push('/error');
        }
    };

    useEffect(() => {
        if (currentShow) {
            setFavourite(
                favourites.shows
                    ? favourites.shows.find(
                          item =>
                              item.id.toString() === currentShow.id.toString(),
                      )
                    : false,
            );
            setPageState('ready');

            let season =
                currentShow.seasons &&
                currentShow.seasons.length > 0 &&
                currentShow.seasons[0].number;

            if (params.season && season) {
                let seasonIndex = currentShow.seasons.findIndex(
                    item => item.number.toString() === params.season.toString(),
                );
                season = seasonIndex >= 0 ? params.season : season;
            }

            handleChangeSeason(season);
        }
        // eslint-disable-next-line
    }, [currentShow]);

    useEffect(() => {
        currentSeason &&
            history.replace(
                `/shows/${params.show}/season/${currentSeason.number}`,
            );

        //eslint-disable-next-line
    }, [currentSeason]);

    const handleClickEp = content_id => {
        history.push(`/play/${content_id}`);
    };

    const handleToggleFavorite = e => {
        !favourite
            ? dispatch(FavouriteActions.addShowFavourite(currentShow))
            : dispatch(FavouriteActions.removeShowFavourite(currentShow));

        setAnimateIcon(!favourite);

        setTimeout(
            dispatch(
                ToastActions.show(
                    <>
                        <FavouriteIcon
                            src={HeartIcon}
                            active={!favourite ? 'true' : null}
                        />
                        {t(`shows.${!favourite ? 'favorited' : 'unfavorited'}`)}
                    </>,
                ),
            ),
            !favourite ? 600 : 0,
        );

        setFavourite(!favourite);
    };

    const handleChangeSeason = seasonNumber => {
        if (!seasonNumber) return;

        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                interaction: 'select season click',
                domain: 'cnplus.com',
                brand: 'cartoon network',
                server: 'cnplus',
                platform: 'web',
                appname: 'app|cnplus|web',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                interactionevent: '1',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                userlang: getCurrentLang(),
                franchise: currentShow.title,
                contenttype: 'show',
                contenttitle: currentShow.title,
            },
        });

        if (
            currentShow.seasons &&
            currentShow.seasons.find(
                item => item.number.toString() === seasonNumber.toString(),
            )
        ) {
            const currSeason = currentShow.seasons.find(
                item => item.number.toString() === seasonNumber.toString(),
            );

            setCurrentSeason(currSeason);

            setEpisodes(currSeason.vods);
        }
    };

    const trackSeasonPickerOpened = () => {
        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                interaction: 'season selector click',
                domain: 'cnplus.com',
                brand: 'cartoon network',
                server: 'cnplus',
                platform: 'web',
                appname: 'app|cnplus|web',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                interactionevent: '1',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                userlang: getCurrentLang(),
                franchise: currentShow.title,
                contenttype: 'show',
                contenttitle: currentShow.title,
            },
        });
    };

    const renderEpisodes = () => {
        if (
            pageState === 'ready' &&
            currentShow &&
            currentShow.seasons.length === 0
        ) {
            return null;
        }
        if (pageState !== 'ready' || !episodes) {
            return <Loader />;
        } else if (pageState === 'ready') {
            if (isMobile) {
                return (
                    <Padded>
                        <ContentRow
                            width="calc(100%)"
                            margin="0px"
                            items={episodes}
                            onClickContent={handleClickEp}
                        />
                    </Padded>
                );
            } else {
                return (
                    <Padded>
                        <VideosGrid
                            items={episodes}
                            onClickItem={handleClickEp}
                        />
                    </Padded>
                );
            }
        }
    };

    const formatBundleName = item => {
        let item_subtype = item.type;
        let season_name = t('shows.season');

        if (item_subtype === 'BUNDLE_OF_SHORTS') {
            season_name = t('shows.shorts');
        } else if (item_subtype === 'BUNDLE_OF_MINISODES') {
            season_name = t('shows.minisodes');
        }

        season_name = season_name.toLocaleLowerCase(localStorage.i18nextLng);

        let season_number = item.number;

        if (
            item_subtype === 'BUNDLE_OF_SHORTS' ||
            item_subtype === 'BUNDLE_OF_MINISODES'
        ) {
            season_number = item.number > 1 ? item.number : '';
        }

        return `${season_name} ${season_number}`;
    };

    return (
        <>
            <Helmet>
                <title>Cartoon Network App</title>
            </Helmet>
            <Container>
                {currentShow === undefined ? (
                    <Loader margin="auto" padding="128px 0px" />
                ) : (
                    <>
                        <Helmet>
                            <title>
                                {`Assista ${capitalize(
                                    currentShow.title,
                                    true,
                                )} ${params.season &&
                                    currentSeason &&
                                    `- ${t('shows.season')} ${
                                        currentSeason.number
                                    }`} | Cartoon Network App`}
                            </title>
                            <meta
                                name="description"
                                content={currentShow.description}
                            ></meta>
                        </Helmet>

                        <ShowBackground
                            url={contentUrlSolver(
                                config,
                                currentShow.image,
                                'CHARECTER-MAIN',
                                'png',
                            )}
                            logged={
                                (config.btw && config.btw.enabled) ||
                                (user && user.jwtToken)
                            }
                        >
                            <ShowLogo
                                src={contentUrlSolver(
                                    config,
                                    currentShow.image,
                                    'SHOW-LOGO',
                                    'png',
                                )}
                            />
                            <ShowBlob
                                src={require('./../assets/show_blob.png')}
                            />
                            {user && user.jwtToken && (
                                <FavouriteButton
                                    active={favourite}
                                    onClick={handleToggleFavorite}
                                >
                                    <AnimatedHeartIcon
                                        src={HeartIcon}
                                        data-animated={
                                            animateIcon ? 'true' : 'false'
                                        }
                                        width="100%"
                                    />
                                </FavouriteButton>
                            )}
                        </ShowBackground>
                        <Separator />

                        {((config.btw && config.btw.enabled) ||
                            (user && user.jwtToken)) && (
                            <TitleContainer>
                                <Title>
                                    {['xs', 'mobile', 'sm', 'md'].includes(
                                        breakpoint,
                                    ) && currentShow.title}
                                </Title>
                                {currentShow.seasons &&
                                    currentShow.seasons.length > 1 && (
                                        <ButtonsRowStyled>
                                            <Dropdown
                                                value={
                                                    currentSeason &&
                                                    formatBundleName(
                                                        currentSeason,
                                                    )
                                                }
                                                onActive={
                                                    trackSeasonPickerOpened
                                                }
                                                onChange={handleChangeSeason}
                                                options={
                                                    currentShow.seasons &&
                                                    currentShow.seasons
                                                        .sort((a, b) =>
                                                            a.type ===
                                                            'BUNDLE_OF_MINISODES'
                                                                ? 1
                                                                : a.type ===
                                                                  'BUNDLE_OF_SHORTS'
                                                                ? -1
                                                                : 0,
                                                        )
                                                        .sort((a, b) => {
                                                            if (
                                                                a.type ===
                                                                b.type
                                                            ) {
                                                                return a.number >
                                                                    b.number
                                                                    ? -1
                                                                    : 1;
                                                            }
                                                            return 0;
                                                        })
                                                        .reverse()
                                                        .reduce((acc, curr) => {
                                                            acc.push({
                                                                label: (
                                                                    <>
                                                                        {formatBundleName(
                                                                            curr,
                                                                        )}
                                                                        {currentSeason &&
                                                                            curr
                                                                                .metadata
                                                                                .contentId ===
                                                                                currentSeason
                                                                                    .metadata
                                                                                    .contentId && (
                                                                                <CheckmarkIcon
                                                                                    title=""
                                                                                    aria-hidden="true"
                                                                                />
                                                                            )}
                                                                    </>
                                                                ),
                                                                value:
                                                                    curr.number,
                                                            });
                                                            return acc;
                                                        }, [])
                                                }
                                            />
                                        </ButtonsRowStyled>
                                    )}
                            </TitleContainer>
                        )}

                        {((config.btw && config.btw.enabled) ||
                            (user && user.jwtToken)) &&
                            renderEpisodes()}

                        <About
                            id="about"
                            logged={
                                (config.btw && config.btw.enabled) ||
                                (user && user.jwtToken)
                            }
                        >
                            <AboutTitle>{currentShow.title}</AboutTitle>
                            <Description>{currentShow.description}</Description>
                        </About>

                        {!isMobile && (
                            <EndPoster
                                src={contentUrlSolver(
                                    config,
                                    currentShow.image,
                                    'ABOUT-SHOW',
                                    'png',
                                    '?imwidth=500',
                                )}
                                alt={currentShow.title}
                            />
                        )}

                        {!(config.btw && config.btw.enabled) &&
                            (!user || !user.jwtToken) && (
                                <FavoriteShows>
                                    <Col>
                                        <img
                                            width="100%"
                                            src={Devices}
                                            alt="Available devices"
                                        />
                                        <p>Also available for download on </p>
                                        <Row margin={'0 0 20px 0'} rtl>
                                            <img
                                                src={GooglePlay}
                                                height="44px"
                                                alt="Get it on GooglePlay"
                                            />
                                            <img
                                                src={Apple}
                                                height="44px"
                                                alt="Get it on AppStore"
                                            />
                                        </Row>
                                    </Col>
                                    <Col>
                                        <div>
                                            <h1>
                                                Your favourite shows on the go!
                                            </h1>
                                            <p>
                                                Watch TV shows on your phone,
                                                tablet, laptop, and TV without
                                                paying more.
                                            </p>
                                        </div>
                                    </Col>
                                </FavoriteShows>
                            )}
                    </>
                )}
            </Container>
        </>
    );
};

export default ShowDetails;
